import React from "react";
import { IUserActivity, UserActivityApi, UserActivityReason } from "./api/UserActivityApi";
import { handleErrorMessage } from "./component/ErrorDialog";

export enum ActivityEnum {
    Admin = "admin",
    AiChat = "ai_chat",
    Comment = "comment",
    CustomSentence = "custom_sentence",
    Home = "home",
    Investor = "investor",
    Leaderboard = "leaderboard",
    Lesson = "lesson",
    Settings = "settings",
}

export enum AdminSubactivityEnum {
    HomePage = "homepage",
    CommentReport = "comment_report",
    CourseOffering = "course_offering",
    CreateLesson = "create_lesson",
    DialogPlan = "dialog_plan",
    RegenerateAudio = "regenerate_audio",
    ManualGradeAudio = "manual_grade_audio",
    SentenceScoreReport = "sentence_score_report",
    UserDialogPromptReport = "user_dialog_prompt_report",
    UserAdmin = "user_admin"
}
export enum AiChatEnum {
    Conversation = "conversation",
    OfficeHour = "office_hour",
}

const IDLE_TIMEOUT_MS = 59000;      // 1 minute
const FLUSH_INTERVAL_MS = 60000;    // flush every 60s (tune as needed)
const MIN_SECONDS_TO_SEND = 5;      // was 5; consider 0 if you want every visit

export function useUserActivityTracker(activityName: ActivityEnum, subActivityName: AdminSubactivityEnum | AiChatEnum | null = null, courseId: number | null = null, enabled: boolean = true) {

    const activityStartRef = React.useRef<number | null>(null);   // when this ativity started
    const lastActivityRef = React.useRef<number>(Date.now());  // last user interaction timestamp
    const activeMsTotalRef = React.useRef<number>(0);        // active ms accumulated in current activity (excludes idle)
    const isIdleRef = React.useRef<boolean>(false);
    const idleTimeoutRef = React.useRef<number | null>(null);
    const intervalRef = React.useRef<number | null>(null);

    const prevActivityNameRef = React.useRef<ActivityEnum>(activityName);
    const prevSubActivityNameRef = React.useRef<AdminSubactivityEnum | AiChatEnum | null>(subActivityName);
    const prevCourseIdRef = React.useRef<number | null>(courseId);

    const sessionIdRef = React.useRef<string | null>(null);

    if (subActivityName != null && courseId != null) {
        throw new Error(`cannot have both subaactivityName ${subActivityName} and courseId ${courseId}`)
    }

    const sendUpdateBeacon = React.useCallback(async (reason: UserActivityReason) => {
        if (!enabled) return;
        const start = activityStartRef.current;
        if (start == null) return;

        const now = Date.now();

        // If currently active, include time since lastActivity in this activity
        if (!isIdleRef.current) {
            activeMsTotalRef.current += now - lastActivityRef.current;
            lastActivityRef.current = now;
        }

        // too short? bounced? don't log it.
        const totalSec = activeMsTotalRef.current / 1000;
        if (totalSec >= MIN_SECONDS_TO_SEND) {

            const prevActivityName = prevActivityNameRef.current;
            const prevSubActivityName = prevSubActivityNameRef.current;
            const prevCourseId = prevCourseIdRef.current;

            const activity: IUserActivity = {
                id: 0,
                user_id: 0,
                activity_name: prevActivityName,
                subactivity_name: prevCourseId != null ? `course_id_${prevCourseId}` : prevSubActivityName ? prevSubActivityName : null,
                start_time: new Date(start),
                end_time: new Date(now),
                activity_sec: totalSec,
                session_id: sessionIdRef.current
            };

            // Prefer beacon when leaving; otherwise your API can decide how to send.
            // Make sure addWithBeacon JSON.stringifies internally and has a fetch keepalive fallback.
            void UserActivityApi.updateBeacon(activity, reason).catch((err) => {
                handleErrorMessage(err, () => { }, history, location);
            });
        }
    }, [enabled]);

    const beginActivity = React.useCallback(() => {
        const now = Date.now();
        activityStartRef.current = now;
        lastActivityRef.current = now;
        activeMsTotalRef.current = 0;
        isIdleRef.current = false;
        sessionIdRef.current = uuidv4(); // new row next time we send

        if (idleTimeoutRef.current) clearTimeout(idleTimeoutRef.current);
        idleTimeoutRef.current = window.setTimeout(() => {
            // entering idle: send what we have up to now, then stay idle (no accumulation)
            isIdleRef.current = true;
            void sendUpdateBeacon("idle");
        }, IDLE_TIMEOUT_MS);
    }, [sendUpdateBeacon]);

    const markActive = React.useCallback(() => {
        const now = Date.now();

        if (isIdleRef.current) {
            // Just came back — don't accumulate idle time
            isIdleRef.current = false;
        } else {
            // Accumulate only if previously active
            activeMsTotalRef.current += now - lastActivityRef.current;
        }

        lastActivityRef.current = now;

        // Reset idle detection timer
        if (idleTimeoutRef.current) clearTimeout(idleTimeoutRef.current);
        idleTimeoutRef.current = window.setTimeout(() => {
            isIdleRef.current = true;
            void sendUpdateBeacon("idle");
        }, IDLE_TIMEOUT_MS);
    }, [sendUpdateBeacon]);

    React.useEffect(() => {
        if (!enabled) return;
        // initialize
        beginActivity();
        markActive();

        const activityEvents = ["mousemove", "keydown", "scroll", "touchstart", "click"];
        for (const event of activityEvents) {
            window.addEventListener(event, markActive, { passive: true });
        }
        // Periodic flush so long sessions get recorded
        intervalRef.current = window.setInterval(() => void sendUpdateBeacon("interval"), FLUSH_INTERVAL_MS);

        // when hidden, emit a flush with reset=false
        const onVisibility = () => { if (document.visibilityState === "hidden") void sendUpdateBeacon("hidden"); };
        window.addEventListener("visibilitychange", onVisibility);

        // Most reliable on iOS/Safari when leaving or closing
        const onPageHide = () => { void sendUpdateBeacon("unmount"); };
        window.addEventListener("pagehide", onPageHide);

        // Keep beforeunload as a last resort
        const onBeforeUnload = () => sendUpdateBeacon("unmount");
        window.addEventListener("beforeunload", onBeforeUnload);

        return () => {
            // Finalize current slice and clean up
            void sendUpdateBeacon("unmount");
            if (intervalRef.current) clearInterval(intervalRef.current);
            if (idleTimeoutRef.current) clearTimeout(idleTimeoutRef.current);
            window.removeEventListener("visibilitychange", onVisibility);
            window.removeEventListener("pagehide", onPageHide);
            window.removeEventListener("beforeunload", onBeforeUnload);
            for (const event of activityEvents) {
                window.removeEventListener(event, markActive as any);
            }
        };
    }, [enabled, markActive, sendUpdateBeacon, beginActivity]);

    // When key changes, finalize the current row and start a new one
    React.useEffect(() => {
        const changed =
            prevActivityNameRef.current !== activityName ||
            prevSubActivityNameRef.current !== subActivityName ||
            prevCourseIdRef.current !== courseId;

        if (changed) {
            void sendUpdateBeacon("switch");      // last write for the old key (same row)
            beginActivity();                // start a new row
        }

        // update all previous refs
        prevActivityNameRef.current = activityName;
        prevSubActivityNameRef.current = subActivityName;
        prevCourseIdRef.current = courseId;

    }, [activityName, subActivityName, courseId, sendUpdateBeacon, beginActivity]);

}


function uuidv4(): string {
    // Best case: built-in
    if (typeof crypto !== "undefined" && typeof (crypto as any).randomUUID === "function") {
        return (crypto as any).randomUUID();
    }

    // Get 16 random bytes
    const bytes = new Uint8Array(16);
    if (typeof crypto !== "undefined" && typeof crypto.getRandomValues === "function") {
        crypto.getRandomValues(bytes);
    } else {
        // Last-resort fallback; not cryptographically strong
        for (let i = 0; i < 16; i++) bytes[i] = (Math.random() * 256) | 0;
    }

    // RFC 4122 v4
    bytes[6] = (bytes[6] & 0x0f) | 0x40;
    bytes[8] = (bytes[8] & 0x3f) | 0x80;

    // Hex lookup (avoids padStart)
    const lut: string[] = [];
    for (let i = 0; i < 256; i++) lut[i] = (i + 0x100).toString(16).substr(1);

    return (
        lut[bytes[0]] + lut[bytes[1]] + lut[bytes[2]] + lut[bytes[3]] + "-" +
        lut[bytes[4]] + lut[bytes[5]] + "-" +
        lut[bytes[6]] + lut[bytes[7]] + "-" +
        lut[bytes[8]] + lut[bytes[9]] + "-" +
        lut[bytes[10]] + lut[bytes[11]] + lut[bytes[12]] + lut[bytes[13]] + lut[bytes[14]] + lut[bytes[15]]
    );
}
